import { AxiosError } from 'axios'
import queryString from 'query-string'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useBoolean } from 'react-use'

import authentication from 'api/authentication'
import { CompanyListForm } from 'components/authentication/CompanyListForm'
import BusyOverlay from 'components/common/BusyOverlay'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { messageFromResponseError } from 'utils/strings'

function getStringOrFirstArrayItem(item: string | string[]) {
  return Array.isArray(item) ? item[0] : item
}

export const GoogleCallback: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { startSession } = useAuth()

  /** Small hack to simulate componentDidMount (built-in class Component method) */
  const [hasMounted, setHasMounted] = useBoolean(false)

  const [isSubmitting, setIsSubmitting] = useBoolean(false)
  const [accessToken, setAccessToken] = useState('')
  const [nextPathname, setNextPathname] = useState('')
  const [companies, setCompanies] = useState([])

  const handleError = (err: AxiosError, defaultText: string) => {
    const errorText = messageFromResponseError(err, defaultText)

    toast({
      type: 'error',
      text: errorText,
      time: 10000,
      testKey: 'signin'
    })
  }

  // We don't wrap this function in a try/catch because we expect errors
  // to be caught at the caller
  const handleGoogleLogin = useCallback(
    async (companyName?: string) => {
      setIsSubmitting(true)

      const credentials = await authentication.signinWithGoogle({
        accessToken,
        companyName
      })

      if (credentials && credentials.result === 'chooseCompany') {
        setIsSubmitting(false)
        setCompanies(credentials.companies)
      } else {
        await startSession(credentials)

        if (nextPathname !== location.pathname) {
          navigate(nextPathname || '/')
        }
      }
    },
    [
      accessToken,
      location.pathname,
      navigate,
      nextPathname,
      setIsSubmitting,
      startSession
    ]
  )

  const handleLogin = async (companyName: string) => {
    try {
      await handleGoogleLogin(companyName)
    } catch (err) {
      setIsSubmitting(false)
      handleError(err, 'Unable to authenticate using Google.')
    }
  }

  const handleBack = () => {
    navigate('/')
  }

  useEffect(() => {
    try {
      const { hash } = location
      const parsed = queryString.parse(hash)
      const accessToken = getStringOrFirstArrayItem(parsed.access_token)
      const googleState = getStringOrFirstArrayItem(parsed.state)

      setAccessToken(() => accessToken)

      // If there's a path to forward to after sign in, grab it from the URL
      if (googleState?.includes('"nextPathname"')) {
        setNextPathname(() => JSON.parse(googleState).nextPathname)
      }
    } catch (err) {
      setIsSubmitting(false)
      handleError(err, 'Unable to authenticate using Google.')
      navigate('/')
    }
  }, [location, navigate, setIsSubmitting])

  useEffect(() => {
    // When the component "mounts", we make an initial request to the Gremlin API
    // to get a Gremlin credential using Google's access token. If the result is
    // a list of companies, we set the state and wait until the user selects a
    // company at which point we will invoke handleGoogleLogin again.
    const handleMount = async () => {
      if (accessToken.length === 0 || hasMounted) {
        return
      }

      try {
        await handleGoogleLogin()
        setHasMounted(true)
      } catch (err) {
        setHasMounted(true)
        setIsSubmitting(false)
        handleError(err, 'Unable to authenticate using Google.')
        navigate('/')
      }
    }

    handleMount()
  }, [
    accessToken,
    handleGoogleLogin,
    hasMounted,
    navigate,
    setHasMounted,
    setIsSubmitting,
    setAccessToken,
    setNextPathname
  ])

  return companies?.length ? (
    <CompanyListForm
      companies={companies}
      handleBack={handleBack}
      handleCompanySelection={(companyName) => handleLogin(companyName)}
      submitting={isSubmitting}
    />
  ) : (
    <BusyOverlay shown={true} text="Authorizing" />
  )
}
